import {isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

import {AuditCategoryOfStructure} from '../../../types/AuditCategoryOfStructure';
import {AuditParamType} from '../types/AuditParamType';
import {getParamFromCategory} from './getParamFromCategory';

const MIN_DAMAGE_PHOTO_PARAMS = 2;

/**
 * A category is safe to hold a damage — and therefore safe to offer as a move target — only when
 * it has everything `ListOfDamages`/`DamagePhotos` assume is present: a damage-type param, a
 * comment param, and both photo slots (location + detail). Moving a damage under a category
 * missing any of these would silently drop it from the damage list, orphan its comment on the
 * source part forever, or crash the list on an undefined photo param.
 */
const isDamageCategory = (category: AuditCategoryOfStructure): boolean => {
  const photoParamCount =
    category.paramDefinitions?.relatedActions?.filter(
      (param) => param.type === AuditParamType.PHOTO
    ).length ?? 0;

  return (
    isNotNil(getParamFromCategory(category, AuditParamType.SELECTABLE_BUTTON)) &&
    isNotNil(getParamFromCategory(category, AuditParamType.ENHANCED_COMMENT)) &&
    photoParamCount >= MIN_DAMAGE_PHOTO_PARAMS
  );
};

/** Whether `categoryId` appears anywhere in the category's subtree (including the category itself). */
const containsCategory = (category: AuditCategoryOfStructure, categoryId: string): boolean =>
  category.id === categoryId ||
  Boolean(category.childCategories?.some((child) => containsCategory(child, categoryId)));

/**
 * Recursively collects the leaf categories of the given subtree, mirroring `ListOfDamages`'s own
 * traversal: an invisible category — and everything beneath it — is skipped entirely, not merely
 * excluded if it happens to be a leaf. Otherwise an invisible ancestor would still surface the
 * damage-capable leaves underneath it as move targets, even though the damage list never renders
 * them.
 */
const flattenLeafCategories = (
  categories: AuditCategoryOfStructure[]
): AuditCategoryOfStructure[] =>
  categories
    .filter((category) => category.visible)
    .flatMap((category) =>
      category.childCategories?.length ? flattenLeafCategories(category.childCategories) : category
    );

/**
 * Returns the parts a damage record can be moved to: every visible leaf damage category within
 * the damage's own top-level section (the inspection tab), however deeply nested. The current
 * category is always included so it can be pre-selected in the part dropdown. Returns an empty
 * list when the current category is not a damage leaf.
 *
 * Scoped to the section on purpose: exterior and interior parts stay separated, so an exterior
 * damage never offers an interior part as a target. Within the section the list is complete —
 * T20-137836 widened it past the immediate siblings to every subsection.
 *
 * Deliberately not filtered by the record's damage type. A part that does not offer that type is
 * still a valid target (T20-137836): the move clears the type and the inspector picks one the
 * target does offer before saving. `supportsDamageType` decides which targets warn about that
 * first — this list decides only which parts can hold a damage at all.
 */
export const getDamageMoveTargetCategories = (
  structureCategories: AuditCategoryOfStructure[] | Nullish,
  categoryId: string
): AuditCategoryOfStructure[] => {
  if (isNilOrEmpty(structureCategories)) {
    return [];
  }

  const section = (structureCategories ?? []).find((category) =>
    containsCategory(category, categoryId)
  );

  // `flattenLeafCategories` already drops invisible categories (and their subtrees), so only the
  // damage-shape check is left to apply here.
  const damageParts = flattenLeafCategories(section ? [section] : []).filter(isDamageCategory);

  if (!damageParts.some((category) => category.id === categoryId)) {
    return [];
  }

  return damageParts;
};
